<template>
  <div class="tt-root" ref="root" :style="{ height: (STAGE_H * scale) + 'px' }">
    <div class="tt-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Broadcast side view of the table: one flat SVG where every point
           goes through the same perspective projection of the table top
           (proj), heights (legs, net, ball) scaled by the depth. -->
      <svg class="tt-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern v-if="crowd" :id="uid + 'crowd'" patternUnits="userSpaceOnUse" width="90" height="45">
            <image :href="crowd" x="0" y="0" width="90" height="45" />
          </pattern>
          <linearGradient :id="uid + 'floor'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#8a2e33" />
            <stop offset="1" stop-color="#6e2227" />
          </linearGradient>
          <linearGradient :id="uid + 'top'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#2459b0" />
            <stop offset="1" stop-color="#1b4892" />
          </linearGradient>
          <!-- Serve fan, in the fan's own coordinates (apex at 0,0). -->
          <radialGradient :id="uid + 'fan'" cx="0" cy="0" :r="FAN_R" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#8fd6ff" stop-opacity="0.4" />
            <stop offset="1" stop-color="#6cc3f5" stop-opacity="0" />
          </radialGradient>
          <radialGradient :id="uid + 'ping'" cx="0" cy="0" :r="FAN_R" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#b5e8ff" stop-opacity="0.9" />
            <stop offset="0.55" stop-color="#7fd0ff" stop-opacity="0.5" />
            <stop offset="1" stop-color="#6cc3f5" stop-opacity="0.05" />
          </radialGradient>
          <radialGradient :id="uid + 'ball'" cx="0.35" cy="0.35" r="0.7">
            <stop offset="0" stop-color="#fff7ea" />
            <stop offset="1" stop-color="#f3a14a" />
          </radialGradient>
        </defs>

        <!-- Stands. -->
        <rect x="0" y="0" :width="STAGE_W" :height="STAGE_H" fill="#15151c" />
        <rect v-if="crowd" x="0" y="0" :width="STAGE_W" :height="STAGE_H" :fill="'url(#' + uid + 'crowd)'" opacity="0.8" />

        <!-- Playing area floor, then the dark foreground band. -->
        <polygon :points="surround" :fill="'url(#' + uid + 'floor)'" />
        <rect x="0" :y="FRONT_Y" :width="STAGE_W" :height="STAGE_H - FRONT_Y" fill="#1a2236" />
        <line x1="0" :y1="FRONT_Y" :x2="STAGE_W" :y2="FRONT_Y" stroke="rgba(255,255,255,0.3)" stroke-width="1" />

        <!-- Barriers around the playing area. -->
        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#13306b" top-fill="#1f438a" />

        <!-- Logo painted on the floor behind the table (in front of it there is no room). -->
        <image v-if="showSirplayLogo" :href="PITCH_LOGO" :transform="logoDecal" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" />

        <!-- Table: shadow on the floor, legs, top, near edge. -->
        <polygon :points="table.shadow" fill="rgba(0,0,0,0.3)" />
        <g stroke="#1f2937" stroke-width="3" stroke-linecap="round">
          <path v-for="(d, i) in table.legs" :key="'leg' + i" :d="d" />
        </g>
        <polygon :points="table.top" :fill="'url(#' + uid + 'top)'" />
        <polygon :points="table.apron" fill="#123570" />

        <!-- Serve fan: from the server toward the other half (hidden while
             a point is being played), with two radar-like "ping" waves. -->
        <g v-if="fan && !rally && !finished" :transform="'translate(' + pt(fan.apex) + ')'">
          <path :d="fan.d" :fill="'url(#' + uid + 'fan)'" />
          <g v-for="w in 2" :key="'ping' + w" opacity="0">
            <animateTransform attributeName="transform" type="scale" values="0.08;1" keyTimes="0;1" calcMode="spline" keySplines="0.2 0.7 0.4 1" dur="1.8s" :begin="(w - 1) * 0.9 + 's'" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;0.9;0" keyTimes="0;0.12;0.7;1" dur="1.8s" :begin="(w - 1) * 0.9 + 's'" repeatCount="indefinite" />
            <path :d="fan.d" :fill="'url(#' + uid + 'ping)'" />
          </g>
        </g>

        <!-- Table lines: white edges and the centre line. -->
        <g fill="none" stroke="rgba(255,255,255,0.95)" stroke-width="1.4" stroke-linejoin="miter">
          <path v-for="(d, i) in tableLines" :key="'l' + i" :d="d" />
        </g>

        <!-- Bounce marks under the net, the ball above it. -->
        <g v-if="rally">
          <ellipse v-for="b in rally.bounces" :key="b.key" :cx="b.x" :cy="b.y" rx="5" ry="1.8" class="tt-bounce" />
        </g>

        <!-- Net: mesh, white tape on top, posts at both ends. -->
        <polygon :points="net.mesh" fill="rgba(226,232,240,0.35)" stroke="rgba(15,23,42,0.8)" stroke-width="0.6" />
        <line :x1="net.topFar.x" :y1="net.topFar.y" :x2="net.topNear.x" :y2="net.topNear.y" stroke="#fff" stroke-width="1.6" />
        <line :x1="net.baseFar.x" :y1="net.baseFar.y" :x2="net.topFar.x" :y2="net.topFar.y" stroke="#0a0a0a" stroke-width="1.6" />
        <line :x1="net.baseNear.x" :y1="net.baseNear.y" :x2="net.topNear.x" :y2="net.topNear.y" stroke="#0a0a0a" stroke-width="2.2" />

        <g v-if="ball && !rally && !finished">
          <ellipse :cx="ball.shadow.x" :cy="ball.shadow.y" rx="2.6" ry="1" fill="rgba(0,0,0,0.3)" class="tt-ball-idle-shadow" />
          <g class="tt-ball-idle">
            <circle :cx="ball.x" :cy="ball.y" r="3" :fill="'url(#' + uid + 'ball)'" />
          </g>
        </g>
        <g v-if="rally">
          <ellipse :cx="rally.shadow.x" :cy="rally.shadow.y" :rx="2.6 * rally.k" :ry="1 * rally.k" fill="rgba(0,0,0,0.35)" :opacity="rally.opacity" />
          <circle :cx="rally.ball.x" :cy="rally.ball.y" :r="3 * rally.k" :fill="'url(#' + uid + 'ball)'" :opacity="rally.opacity" />
        </g>
      </svg>

      <!-- Flat overlays (HTML, so text renders crisply at every scale). -->
      <div class="tt-score">
        <span class="tt-score-games">{{ curGames.h }}-{{ curGames.a }}</span>
        <span class="tt-score-pts">
          <span :key="'h' + popKey" :class="{ 'is-pop': popSide === '1' }">{{ curH }}</span>
          <span :key="'a' + popKey" :class="{ 'is-pop': popSide === '2' }">{{ curA }}</span>
        </span>
        <span v-if="statusText" class="tt-score-status">{{ statusText }}</span>
      </div>
      <div class="tt-label tt-label--home">{{ homeName }}</div>
      <div class="tt-label tt-label--away">{{ awayName }}</div>
      <div v-if="(servingSide === '1' || servingSide === '2') && !finished" class="tt-serving">
        <span class="tt-serving-label">{{ $t('statistics.atserve') }}</span>
        <span class="tt-serving-name">{{ servingSide === '1' ? homeName : awayName }}</span>
        <span class="tt-serving-bar" :class="servingSide === '1' ? 'is-home' : 'is-away'"></span>
      </div>

      <!-- Yellow card / time-out: a short banner over the table. -->
      <transition name="tt-flash">
        <div v-if="flash && !finished" :key="flash.key" class="tt-flash" :style="{ '--side-color': sideColor(flash.side) }">
          <span v-if="flash.type === 6" class="tt-flash-card"></span>
          <v-icon v-else size="16" color="#fff">mdi-timer-outline</v-icon>
          <span class="tt-flash-text">{{ flash.text }}</span>
          <span class="tt-flash-name">{{ flash.side === '1' ? homeName : awayName }}</span>
        </div>
      </transition>

      <!-- Point / set won: full-stage overlay, then the score box updates. -->
      <transition name="tt-overlay">
        <div v-if="overlay" :key="overlay.key" class="tt-point-overlay" :style="{ '--side-color': overlay.color }">
          <div class="tt-point-title">{{ overlay.title }}</div>
          <div class="tt-point-team">{{ overlay.name }}</div>
          <div class="tt-point-score">
            <span :class="{ 'is-won': overlay.side === '1' }">{{ overlay.h }}</span>
            <span class="tt-point-sep">-</span>
            <span :class="{ 'is-won': overlay.side === '2' }">{{ overlay.a }}</span>
          </div>
        </div>
      </transition>

      <!-- Match over: big badge that stays up. -->
      <transition name="tt-final">
        <div v-if="finished" class="tt-final">
          <div class="tt-final-badge">
            <svg class="tt-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="tt-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div v-if="winnerName" class="tt-final-winner">{{ winnerName }}</div>
            <div v-if="games.length" class="tt-final-sets">
              <span v-for="(g, i) in games" :key="'fs' + i">{{ g.h }}-{{ g.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, pt, seg, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width and zoomed
// in by ZOOM (the extra width is cropped equally off both sides).
const ZOOM = 1.1;
const STAGE_W = 460;
const STAGE_H = 270;
// Table top projection (see makeProjection): u along the table, v across it.
const FAR_Y = 84;
const NEAR_Y = 150;
const FAR_W = 262;
const NEAR_W = 322;
const FAR_CX = 229;
const NEAR_CX = 231;
// Table top height above the floor, and the net's, in px at the far edge.
const LEG_H = 46;
const NET_H = 11;
// Net posts stick out 15.25cm on each side of the 152.5cm wide table.
const NET_OUT = 0.1;
const FRONT_Y = 214;
const FAN_R = 120;
// Floor / barrier corners in screen px.
const FAR_L = [34, 44];
const FAR_R = [426, 44];
const SIDE_L = [0, 104];
const SIDE_R = [STAGE_W, 100];

// Rally timing (ms): much quicker than tennis.
const T_TOSS = 420;
const T_SERVE_OWN = 260;
const T_SERVE_CROSS = 380;
const T_SHOT = 470;
const T_HOP = 220;
const T_OUT = 650;
const OVERLAY_MS = 2400;
const FLASH_MS = 3500;
// A game is won at 11 with a 2-point lead.
const GAME_POINTS = 11;

const rand = (a, b) => a + Math.random() * (b - a);
const pick = list => list[Math.floor(Math.random() * list.length)];
const num = v => parseInt(v) || 0;

const { proj } = makeProjection({ farY: FAR_Y, nearY: NEAR_Y, farW: FAR_W, nearW: NEAR_W, farCx: FAR_CX, nearCx: NEAR_CX });
// A point h px above the table top (negative: below it, down to the floor).
const lift = (u, v, h) => {
  const g = proj(u, v);
  const k = g.w / FAR_W;
  return { x: g.x, y: g.y - h * k, k };
};
// A point on the floor, under (u, v) of the table top.
const floorAt = (u, v) => lift(u, v, -LEG_H);
const onTable = (u, v) => u >= 0 && u <= 1 && v >= 0 && v <= 1;

export default {
  name: 'TableTennisArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    // Points in the game being played.
    pointsH: { type: String, default: '0' },
    pointsA: { type: String, default: '0' },
    // Games won (the scoreboard).
    gamesH: { type: [String, Number], default: 0 },
    gamesA: { type: [String, Number], default: 0 },
    // '1' home / '2' away / null.
    servingSide: { type: String, default: null },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    // Points per game so far, [{ h, a }] (for the final badge).
    games: { type: Array, default: () => [] },
    // Latest incident from the tracker: only yellow cards (6) and
    // time-outs (31) are shown, as a short banner.
    actionType: { type: Number, default: 0 },
    actionSide: { type: String, default: null },
    actionKey: { type: [String, Number], default: '' },
    finished: { type: Boolean, default: false }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      ZOOM,
      FRONT_Y,
      FAN_R,
      crowd: '',
      // Score shown on the table: lags the props while a point is animated.
      curH: this.pointsH,
      curA: this.pointsA,
      curGames: { h: num(this.gamesH), a: num(this.gamesA) },
      curServe: this.servingSide,
      rally: null,
      rallyRaf: null,
      overlay: null,
      overlayTimer: null,
      flash: null,
      flashTimer: null,
      popSide: null,
      popKey: 0,
      // The point that closed a game was already shown as "set won": the
      // games update that follows is then synced without a second overlay.
      pendingGameWin: null
    };
  },
  watch: {
    pointKey(val, old) {
      if (!old || this.finished) {
        this.syncScore();
        return;
      }
      this.onScoreChange();
    },
    // A serve change with no point played (e.g. first feed update).
    servingSide(val) {
      if (!this.rally) this.curServe = val;
    },
    finished(val) {
      if (val) {
        this.stopRally();
        this.syncScore();
      }
    },
    actionKey() {
      const t = this.actionType;
      if ((t !== 6 && t !== 31) || (this.actionSide !== '1' && this.actionSide !== '2')) return;
      this.flash = {
        key: this.actionKey,
        type: t,
        side: this.actionSide,
        text: this.$t(t === 6 ? 'statistics.yellowcard' : 'statistics.timeouts')
      };
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = null; }, FLASH_MS);
    }
  },
  mounted() {
    this.crowd = crowdTexture('arena');
  },
  beforeDestroy() {
    this.stopRally();
    clearTimeout(this.overlayTimer);
    clearTimeout(this.flashTimer);
  },
  methods: {
    pt,
    syncScore() {
      this.curH = this.pointsH;
      this.curA = this.pointsA;
      this.curGames = { h: num(this.gamesH), a: num(this.gamesA) };
      this.curServe = this.servingSide;
    },
    // Works out who won the point from the old/new score, plays the rally,
    // then shows the overlay and moves the score box on.
    onScoreChange() {
      const prev = this.curGames;
      const games = { h: num(this.gamesH), a: num(this.gamesA) };
      let winner = null;
      let kind = 'point';
      if (games.h !== prev.h || games.a !== prev.a) {
        kind = 'game';
        winner = games.h > prev.h ? '1' : (games.a > prev.a ? '2' : null);
        if (winner && winner === this.pendingGameWin) {
          this.pendingGameWin = null;
          this.syncScore();
          return;
        }
      } else {
        const o = { h: num(this.curH), a: num(this.curA) };
        const n = { h: num(this.pointsH), a: num(this.pointsA) };
        if (n.h > o.h && n.a >= o.a) winner = '1';
        else if (n.a > o.a && n.h >= o.h) winner = '2';
        // The point closes the game (11-x with a 2-point lead): show it as
        // a set won straight away.
        if (winner) {
          const w = winner === '1' ? n.h : n.a;
          const l = winner === '1' ? n.a : n.h;
          const closes = w >= GAME_POINTS && w - l >= 2;
          if (closes) kind = 'game';
          this.pendingGameWin = closes ? winner : null;
        }
      }
      if (!winner) {
        this.syncScore();
        return;
      }
      const server = this.curServe === '1' || this.curServe === '2' ? this.curServe : winner;
      this.playRally(server, winner, () => this.showPoint(kind, winner));
    },
    showPoint(kind, winner) {
      this.syncScore();
      let h = this.curH;
      let a = this.curA;
      if (kind === 'game') {
        // Games won, counting this one if the scoreboard hasn't caught up yet.
        const g = { ...this.curGames };
        if (this.pendingGameWin === winner) g[winner === '1' ? 'h' : 'a']++;
        h = g.h;
        a = g.a;
      }
      this.popKey++;
      this.popSide = winner;
      this.overlay = {
        key: this.popKey,
        side: winner,
        title: this.$t(kind === 'game' ? 'statistics.set' : 'statistics.point'),
        name: winner === '1' ? this.homeName : this.awayName,
        color: this.sideColor(winner),
        h,
        a
      };
      clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => { this.overlay = null; }, OVERLAY_MS);
    },
    sideColor(side) {
      if (side === '1') return this.homeColor || '#2d5fae';
      return this.awayColor || '#1c8a8e';
    },
    stopRally() {
      if (this.rallyRaf) cancelAnimationFrame(this.rallyRaf);
      this.rallyRaf = null;
      this.rally = null;
    },
    // Builds the flight segments of a rally that the winner closes out.
    buildRally(server, winner) {
      const other = s => (s === '1' ? '2' : '1');
      // Shot i is hit by the server when i is even: the winner must hit last.
      // Points come quickly: keep rallies short (at most ~4s).
      const counts = winner === server ? [1, 3, 3, 5] : [2, 2, 4, 4];
      const n = pick(counts);
      const spot = this.serveSpot;
      const hitter = side => ({ u: side === '1' ? rand(-0.12, -0.04) : rand(1.04, 1.12), v: rand(0.1, 0.9), h: rand(8, 18) });
      // Bounce on `side`'s own half.
      const half = side => ({ u: side === '1' ? rand(0.08, 0.4) : rand(0.6, 0.92), v: rand(0.12, 0.88), h: 0 });
      const segs = [];
      let from = { u: spot.u, v: spot.v, h: spot.h };
      // Toss: straight up above the server's hand.
      segs.push({ from, to: { ...from, h: spot.h + 20 }, peak: 0, dur: T_TOSS, ease: true });
      from = { ...from, h: spot.h + 20 };
      let side = server;
      for (let i = 0; i < n; i++) {
        const last = i === n - 1;
        let bounce;
        if (i === 0) {
          // Serve: one bounce on the server's half, then one on the other.
          const own = half(server);
          segs.push({ from, to: own, peak: 3, dur: T_SERVE_OWN, bounce: true });
          bounce = half(other(server));
          segs.push({ from: own, to: bounce, peak: NET_H + 4, dur: T_SERVE_CROSS, bounce: true });
        } else {
          bounce = half(other(side));
          segs.push({ from, to: bounce, peak: rand(NET_H + 6, NET_H + 16), dur: T_SHOT, bounce: true });
        }
        if (last) {
          // Winner: the ball skips past the opponent and drops to the floor.
          const prevU = i === 0 ? (server === '1' ? 0.25 : 0.75) : from.u;
          const du = bounce.u - prevU;
          const out = { u: bounce.u + du * 0.9, v: Math.max(-0.4, Math.min(1.4, bounce.v + rand(-0.3, 0.3))), h: -LEG_H };
          segs.push({ from: bounce, to: out, peak: 14, dur: T_OUT, fade: true });
        } else {
          const next = hitter(other(side));
          segs.push({ from: bounce, to: next, peak: 8, dur: T_HOP });
          from = next;
        }
        side = other(side);
      }
      return segs;
    },
    playRally(server, winner, done) {
      this.stopRally();
      if (!this.serveSpot) this.curServe = server;
      const segs = this.buildRally(server, winner);
      let i = 0;
      let t0 = null;
      const bounces = [];
      const frame = ts => {
        if (t0 === null) t0 = ts;
        let s = segs[i];
        let t = (ts - t0) / s.dur;
        while (t >= 1) {
          if (s.bounce) {
            const b = proj(s.to.u, s.to.v);
            bounces.push({ key: ts + '-' + i, x: b.x, y: b.y });
            if (bounces.length > 3) bounces.shift();
          }
          i++;
          if (i >= segs.length) {
            this.stopRally();
            done();
            return;
          }
          t0 += s.dur;
          s = segs[i];
          t = (ts - t0) / s.dur;
        }
        const e = s.ease ? 1 - (1 - t) * (1 - t) : t;
        const u = s.from.u + (s.to.u - s.from.u) * t;
        const v = s.from.v + (s.to.v - s.from.v) * t;
        const h = s.from.h + (s.to.h - s.from.h) * e + 4 * s.peak * t * (1 - t);
        const b = lift(u, v, h);
        // Shadow on the table top while over it, else on the floor.
        const sh = lift(u, v, onTable(u, v) ? 0 : -LEG_H);
        this.rally = {
          ball: { x: b.x, y: b.y - 3 * b.k },
          shadow: { x: sh.x + 1, y: sh.y + 1 },
          k: b.k,
          opacity: s.fade ? 1 - t : 1,
          bounces: bounces.slice()
        };
        this.rallyRaf = requestAnimationFrame(frame);
      };
      this.rallyRaf = requestAnimationFrame(frame);
    }
  },
  computed: {
    logoDecal() {
      return floorLogo(floorAt, 0.5, -1.2, 0.25);
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'tt' + this._uid + '-'; },
    pointKey() {
      return [this.pointsH, this.pointsA, num(this.gamesH), num(this.gamesA)].join('|');
    },
    winnerName() {
      const h = num(this.gamesH);
      const a = num(this.gamesA);
      if (h === a) return '';
      return h > a ? this.homeName : this.awayName;
    },
    statusText() {
      const h = num(this.curH);
      const a = num(this.curA);
      const top = GAME_POINTS - 1;
      if (h >= top && h === a) return this.$t('statistics.deuce');
      if ((h >= top || a >= top) && h !== a) return this.$t('statistics.setpoint');
      return '';
    },
    surround() {
      return [FAR_L, FAR_R, SIDE_R, [STAGE_W, FRONT_Y + 10], [0, FRONT_Y + 10], SIDE_L].map(p => p.join(',')).join(' ');
    },
    boards() {
      const P = p => ({ x: p[0], y: p[1] });
      return [
        makeBoard('far', P(FAR_L), P(FAR_R), 14, 14, 5, 40),
        makeBoard('left', P(SIDE_L), P(FAR_L), 22, 14, 2, 38),
        makeBoard('right', P(FAR_R), P(SIDE_R), 14, 22, 2, 38)
      ];
    },
    table() {
      const poly = list => list.map(pt).join(' ');
      const corners = [[0, 0], [1, 0], [1, 1], [0, 1]];
      const top = corners.map(c => proj(c[0], c[1]));
      const floor = corners.map(c => lift(c[0], c[1], -LEG_H));
      // 2cm thick top: the near edge only.
      const apron = [top[3], top[2], lift(1, 1, -2.4), lift(0, 1, -2.4)];
      const legs = [];
      [0.12, 0.88].forEach(u => {
        [0.1, 0.9].forEach(v => legs.push(seg(lift(u, v, -1), lift(u, v, -LEG_H))));
      });
      return {
        top: poly(top),
        apron: poly(apron),
        shadow: poly(floor.map(p => ({ x: p.x + 4, y: p.y + 2 }))),
        legs
      };
    },
    tableLines() {
      const e = 0.006;
      return [
        'M' + [proj(e, e), proj(1 - e, e), proj(1 - e, 1 - e), proj(e, 1 - e)].map(pt).join(' L') + ' Z',
        seg(proj(e, 0.5), proj(1 - e, 0.5))
      ];
    },
    net() {
      const baseFar = proj(0.5, -NET_OUT);
      const baseNear = proj(0.5, 1 + NET_OUT);
      const topFar = lift(0.5, -NET_OUT, NET_H);
      const topNear = lift(0.5, 1 + NET_OUT, NET_H);
      return {
        baseFar,
        baseNear,
        topFar,
        topNear,
        mesh: [baseFar, baseNear, topNear, topFar].map(pt).join(' ')
      };
    },
    // Where the server holds the ball: just behind their end line, a bit
    // above the table.
    serveSpot() {
      if (this.curServe !== '1' && this.curServe !== '2') return null;
      const home = this.curServe === '1';
      const odd = (num(this.curH) + num(this.curA)) % 2 === 1;
      const u = home ? -0.05 : 1.05;
      const v = odd ? 0.36 : 0.64;
      return { u, v, h: 8, at: lift(u, v, 8), target: proj(home ? 0.78 : 0.22, 1 - v) };
    },
    ball() {
      const s = this.serveSpot;
      if (!s) return null;
      const sh = lift(s.u, s.v, -LEG_H);
      return { x: s.at.x, y: s.at.y - 3, shadow: { x: sh.x + 1, y: sh.y + 1 } };
    },
    fan() {
      if (!this.serveSpot) return null;
      const apex = this.serveSpot.at;
      const t = this.serveSpot.target;
      const c = Math.atan2(t.y - apex.y, t.x - apex.x);
      const half = 18 * Math.PI / 180;
      // Path relative to the apex, so the ping can scale out of it.
      const p1 = { x: FAN_R * Math.cos(c - half), y: FAN_R * Math.sin(c - half) };
      const p2 = { x: FAN_R * Math.cos(c + half), y: FAN_R * Math.sin(c + half) };
      return {
        apex,
        d: `M0,0 L${pt(p1)} A${FAN_R},${FAN_R} 0 0 1 ${pt(p2)} Z`
      };
    }
  }
};
</script>

<style scoped>
.tt-root { position: relative; width: 100%; overflow: hidden; background: #15151c; }
.tt-stage { position: absolute; top: 0; left: 0; width: 460px; height: 270px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.tt-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.tt-score { position: absolute; top: 8px; left: 0; right: 0; display: flex; justify-content: center; align-items: stretch; height: 22px; z-index: 2; }
.tt-score-games { display: flex; align-items: center; padding: 0 7px; background: #1f2937; color: #cbd5e1; font-weight: 700; font-size: var(--font-size-2xl); }
.tt-score-pts { display: flex; align-items: center; gap: 12px; padding: 0 7px; background: #0b0b0b; color: #fff; font-weight: 700; font-size: var(--font-size-5xl); line-height: 1; }
.tt-score-status { display: flex; align-items: center; padding: 0 8px; background: rgba(12, 20, 18, 0.72); color: #e9e33b; font-weight: 700; font-size: var(--font-size-2xl); white-space: nowrap; }

.tt-label { position: absolute; top: 44px; padding: 4px 8px; border-radius: 5px; color: #fff; font-weight: 700; font-size: var(--font-size-3xl); line-height: 1.2; white-space: nowrap; max-width: 150px; overflow: hidden; text-overflow: ellipsis; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35); z-index: 2; }
.tt-label--home { left: 30px; background: #2d5fae; }
.tt-label--away { right: 30px; background: #1c8a8e; }

.tt-serving { position: absolute; left: 0; right: 0; top: 235px; display: flex; justify-content: center; align-items: center; gap: 7px; font-size: var(--font-size-3xl); z-index: 2; }
.tt-serving-label { color: #dbe7f1; font-weight: 600; }
.tt-serving-name { color: #fff; font-weight: 500; }
.tt-serving-bar { width: 2px; height: 15px; }
.tt-serving-bar.is-home { background: #3a6fd0; }
.tt-serving-bar.is-away { background: #1fb2b7; }

/* Idle: the server bounces the ball in the hand before serving. */
.tt-ball-idle { animation: tt-dribble 1s ease-in infinite; }
.tt-ball-idle-shadow { animation: tt-dribble-shadow 1s ease-in infinite; transform-box: fill-box; transform-origin: 50% 50%; }
@keyframes tt-dribble { 0%, 100% { transform: translateY(-7px); animation-timing-function: ease-in; } 50% { transform: translateY(0); animation-timing-function: ease-out; } }
@keyframes tt-dribble-shadow { 0%, 100% { transform: scale(0.7); opacity: 0.6; } 50% { transform: scale(1); opacity: 1; } }

/* Bounce mark left on the table. */
.tt-bounce { fill: none; stroke: rgba(255, 255, 255, 0.85); stroke-width: 1; transform-box: fill-box; transform-origin: 50% 50%; animation: tt-bounce 0.6s ease-out forwards; }
@keyframes tt-bounce { from { transform: scale(0.3); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }

/* Score box: the number that just changed pops. */
.tt-score-pts .is-pop { display: inline-block; color: #e9e33b; animation: tt-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) forwards; }
@keyframes tt-pop { 0% { transform: scale(0.4); } 40% { transform: scale(1.5); } 100% { transform: scale(1); color: #fff; } }

/* Yellow card / time-out banner. */
.tt-flash { position: absolute; left: 50%; top: 172px; transform: translateX(-50%); z-index: 3; display: flex; align-items: center; gap: 7px; padding: 5px 12px; border-radius: 5px; border-left: 3px solid var(--side-color); background: rgba(11, 11, 11, 0.85); color: #fff; font-size: var(--font-size-3xl); white-space: nowrap; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45); }
.tt-flash-card { width: 10px; height: 14px; border-radius: 2px; background: #facc15; }
.tt-flash-text { font-weight: 700; }
.tt-flash-name { opacity: 0.85; }
.tt-flash-enter-active, .tt-flash-leave-active { transition: opacity 0.3s, transform 0.3s; }
.tt-flash-enter, .tt-flash-leave-to { opacity: 0; transform: translateX(-50%) translateY(8px); }

/* Point / set overlay. */
.tt-point-overlay {
  position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
}
.tt-point-overlay::before {
  content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 96px; transform: translateY(-50%) skewY(-4deg);
  background: linear-gradient(90deg, transparent 0%, var(--side-color) 20%, var(--side-color) 80%, transparent 100%); opacity: 0.85;
  animation: tt-band 0.5s cubic-bezier(.2, .9, .3, 1) both;
}
.tt-point-title { position: relative; color: #fff; font-size: var(--font-size-17xl); font-weight: 900; font-style: italic; text-transform: uppercase; text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55); animation: tt-title 0.6s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
.tt-point-team { position: relative; color: #fff; font-size: var(--font-size-4xl); font-weight: 700; opacity: 0.95; animation: tt-rise 0.4s ease-out 0.3s both; }
.tt-point-score { position: relative; display: flex; gap: 8px; margin-top: 4px; padding: 3px 12px; background: #0b0b0b; border-radius: 4px; color: #fff; font-size: var(--font-size-7xl); font-weight: 800; animation: tt-rise 0.4s ease-out 0.45s both; }
.tt-point-score .is-won { display: inline-block; color: #e9e33b; animation: tt-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) 0.7s both; }
.tt-point-sep { opacity: 0.6; }
@keyframes tt-band { from { transform: translateY(-50%) skewY(-4deg) scaleX(0); } to { transform: translateY(-50%) skewY(-4deg) scaleX(1); } }
@keyframes tt-title { from { transform: scale(2.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes tt-rise { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.tt-overlay-enter-active { transition: opacity 0.2s; }
.tt-overlay-leave-active { transition: opacity 0.4s; }
.tt-overlay-enter, .tt-overlay-leave-to { opacity: 0; }

/* Match over. */
.tt-final { position: absolute; inset: 0; z-index: 6; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.tt-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 34px 16px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: tt-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.tt-final-icon { width: 40px; height: 40px; animation: tt-trophy 2s ease-in-out 0.7s infinite; }
.tt-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.tt-final-winner { color: #fff; font-size: var(--font-size-5xl); font-weight: 700; }
.tt-final-sets { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 2px; color: #cbd5e1; font-size: var(--font-size-3xl); font-weight: 700; }
@keyframes tt-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes tt-trophy { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(-3px) rotate(6deg); } }
.tt-final-enter-active { transition: opacity 0.3s; }
.tt-final-enter { opacity: 0; }
.arena-logo { opacity: 0.95; pointer-events: none; }
</style>
